// ============================================================================
// citation-service.ts — Entry points + global state for Citations
// Absorbed from Vue-CitationForm.ts and Vue-Citations.ts
// ============================================================================

import { renderReactComponent } from '@/react-bridge';
import { CitationFormScreen } from '@/components/screens/CitationFormScreen';
import { navigationApi } from '@/services/navigation';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { AccessAuthorities } from '@/models';
import { getModuleType, currentUserHasPermission } from '@/utils/legacy-utils';

import { getCurrentUserId } from '@/utils/auth';


// ── Global State ─────────────────────────────────────────────────────────
// Initialize globals on window (replacing `var` declarations from outFile bundle).
// Use ?? to avoid overwriting if still present during transition.

(window as any).citationParentContext = (window as any).citationParentContext ?? null;
(window as any).citationParentId = (window as any).citationParentId ?? null;
(window as any).currentCitation = (window as any).currentCitation ?? null;
(window as any).citationFormState = (window as any).citationFormState ?? null;

// These are mutable module-level state — read/write via window directly
// (citationParentContext, citationParentId, currentCitation, citationFormState)

// ── Interfaces ───────────────────────────────────────────────────────────

interface CitationFormOptions {
  mode: 'new' | 'edit' | 'view';
  citationId?: number;
  preFilledPersonId?: number;
  preLinkedCallCardId?: number;
  callCardData?: any;
  parentContext?: 'person' | 'callcard' | 'citations';
  parentId?: number;
}

// ── Helpers ──────────────────────────────────────────────────────────────

function applyCallCardDefaults(callCardData: any) {
  const cc = (window as any).currentCitation;
  if (!callCardData || !cc) return;

  cc.propertyLocation = callCardData.callLocFormattedAddress || '';
  cc.offenseLocation = callCardData.callLocFormattedAddress || '';
  cc.cadEventNumber = callCardData.eventId || '';
  if (callCardData.callDateTime) {
    cc.offenseDateTime = new Date(callCardData.callDateTime);
  }
}

function renderCitationForm(mode: string, preFilledPersonId?: number, preLinkedCallCardId?: number, callCardData?: any) {
  const container = document.getElementById('citationFormScreen');
  if (container) {
    (window as any).OrnaReact.renderComponent(
      (window as any).CitationFormScreenReact,
      {
        options: {
          mode: mode,
          citationId: (window as any).currentCitation ? (window as any).currentCitation.id : undefined,
          preFilledPersonId: preFilledPersonId,
          preLinkedCallCardId: preLinkedCallCardId,
          callCardData: callCardData,
          parentContext: (window as any).citationParentContext,
          parentId: (window as any).citationParentId,
        },
      },
      container
    );
  }
  (window as any).citationFormState = {
    isDirty: false,
    destroy() {
      const c = document.getElementById('citationFormScreen');
      if (c) (window as any).OrnaReact.destroyComponent(c);
    },
  };
}

function loadCitation(citationId: number, mode: string) {
  App.setProgressControl();

  api.getCitationById(
    citationId,
    (result: any) => {
      (window as any).currentCitation = new (window as any).Citation(result);
      renderCitationForm(mode, null, null, null);

      // Validate call card context matches citation's linked call card
      if ((window as any).citationParentContext === 'callcard' && (window as any).citationParentId) {
        if ((window as any).currentCitation.callCardId && (window as any).currentCitation.callCardId !== (window as any).citationParentId) {
          console.warn(
            `Citation callCardId (${(window as any).currentCitation.callCardId}) does not match parent call card (${(window as any).citationParentId})`
          );
        }
      }
    },
    (_error: any) => {
      App.showPopupNotification('Citation not found.', 'error');
    }
  );
}

// ── Initialization ───────────────────────────────────────────────────────

function openCitationFormWithOptions(options: CitationFormOptions) {
  App.setProgressControl();

  const mode = options.mode || 'new';
  const citationId = options.citationId;
  const preFilledPersonId = options.preFilledPersonId;
  const preLinkedCallCardId = options.preLinkedCallCardId;
  const callCardData = options.callCardData;

  (window as any).citationParentContext = options.parentContext || null;
  (window as any).citationParentId = options.parentId || null;

  if (mode === 'new') {
    (window as any).currentCitation = new (window as any).Citation(null);
    (window as any).currentCitation.status = 'draft';
    (window as any).currentCitation.dateOfIssuance = new Date();
    (window as any).currentCitation.offenseDateTime = new Date();
    (window as any).currentCitation.issuingOfficerId = getCurrentUserId();

    if (preFilledPersonId) {
      (window as any).currentCitation.npsdPersonId = preFilledPersonId;
    }

    if (preLinkedCallCardId) {
      (window as any).currentCitation.callCardId = preLinkedCallCardId;
    }

    if (callCardData) {
      applyCallCardDefaults(callCardData);
    }

    renderCitationForm(mode, preFilledPersonId, preLinkedCallCardId, callCardData);
  } else if (citationId) {
    loadCitation(citationId, mode);
  }
}

// ── Entry Points ─────────────────────────────────────────────────────────

function Open_CitationForm(params: string) {
  const parts = (params || 'new').split(',');
  const mode = parts[0] || 'new';
  const preFilledPersonId = parts[1] ? parseInt(parts[1], 10) : undefined;
  const preLinkedCallCardId = parts[2] ? parseInt(parts[2], 10) : undefined;
  const citationId = parts[3] ? parseInt(parts[3], 10) : undefined;

  openCitationFormWithOptions({
    mode: mode as 'new' | 'edit' | 'view',
    citationId: citationId,
    preFilledPersonId: preFilledPersonId,
    preLinkedCallCardId: preLinkedCallCardId,
  });
}

function closeCitationForm() {
  if ((window as any).citationFormState) {
    (window as any).citationFormState.isDirty = false;
  }
  history.back();
}

function openNewCitationForCallCard(callCardId?: number) {
  if (!currentUserHasPermission(AccessAuthorities.ROLE_CITATION_CREATE)) {
    App.showPopupNotification('You do not have permission to create citations.', 'error');
    return;
  }

  const ccId = callCardId || (App.currentCC ? App.currentCC.id : null);

  (window as any).citationParentContext = 'callcard';
  (window as any).citationParentId = ccId;

  const moduleType = getModuleType().toUpperCase();
  const params = ccId ? `new,,${ccId}` : 'new';
  navigationApi.raw(
    `/?sectionName=${moduleType}&tab=Citations&function=Open_CitationForm&multiModule=true&params=${params}`
  );
}

/**
 * Create a citation from the call card person grid with the person pre-filled.
 * Called from CallCardPersonGrid.tsx citePerson method.
 */
function createCitationFromCallCardPerson(element: HTMLElement) {
  if (!currentUserHasPermission(AccessAuthorities.ROLE_CITATION_CREATE)) {
    App.showPopupNotification('You do not have permission to create citations.', 'error');
    return;
  }

  const npsdPersonId = element.getAttribute('data-npsd-person-id');
  const ccId = App.currentCC ? App.currentCC.id : null;

  if (!npsdPersonId) {
    App.showPopupNotification('Cannot cite unlinked person.', 'error');
    return;
  }

  (window as any).citationParentContext = 'callcard';
  (window as any).citationParentId = ccId;

  const moduleType = getModuleType().toUpperCase();
  // params format: mode,personId,callCardId,citationId
  const params = `new,${npsdPersonId},${ccId || ''}`;
  navigationApi.raw(
    `/?sectionName=${moduleType}&tab=Citations&function=Open_CitationForm&multiModule=true&params=${params}`
  );
}

function viewCitation(citationId: number, parentContext?: 'person' | 'callcard' | 'citations', parentId?: number) {
  (window as any).citationParentContext = parentContext || null;
  (window as any).citationParentId = parentId || null;

  navigationApi.toCitation(citationId, 'view');
}

function editCitation(citationId: number, parentContext?: 'person' | 'callcard' | 'citations', parentId?: number) {
  (window as any).citationParentContext = parentContext || null;
  (window as any).citationParentId = parentId || null;

  navigationApi.toCitation(citationId, 'edit');
}

// ── Window Registrations ─────────────────────────────────────────────────

(window as any).Open_CitationForm = Open_CitationForm;
(window as any).closeCitationForm = closeCitationForm;
(window as any).openNewCitationForCallCard = openNewCitationForCallCard;
(window as any).createCitationFromCallCardPerson = createCitationFromCallCardPerson;
(window as any).viewCitation = viewCitation;
(window as any).editCitation = editCitation;

// ── Exports ──────────────────────────────────────────────────────────────

export {
  Open_CitationForm,
  closeCitationForm,
  openNewCitationForCallCard,
  createCitationFromCallCardPerson,
  viewCitation,
  editCitation,
};
